import React, { MouseEventHandler, useState } from 'react';
import { AppInternalLink, AppInternalPaths } from '../../primitives/appLink';
import { ApplyMountEffect, cardRevealEffect } from '../../helpers/reveals';
import { ProfessionGalleryModal } from './galleryModal';
import {
  ShIntlMessageDefinition,
  ShText,
  useShIntlLocale,
} from '@shoootin/translations';
import { FrontCommonMessages } from '../../appCommonTranslations';
import { forceImageMaxWidth } from '../../appGatsbyImageUtils';
import useProfessionFirstImages from '../../queries/useProfessionFirstImages';
import { H4 } from '../../primitives/appTitle';
import { getProfessionMessage } from '../../appMessageDefinitions';
import { AppGatsbyImage, AppGatsbyImageFluid } from '../../primitives/appImage';
import { ProfessionShootingType } from '../../appConstants';

type GalleryImageMaxWidth = 400 | 610 | 810;

const GalleryGridCover = ({
  label,
  image,
  onClick,
}: {
  label: ShIntlMessageDefinition;
  image: AppGatsbyImageFluid;
  onClick: MouseEventHandler;
}) => (
  <div className="grid-gallery__cover" onClick={onClick}>
    <a className="grid-gallery__legend">
      <H4>
        <ShText message={label} />
      </H4>
    </a>
    <AppGatsbyImage fluid={image} />
  </div>
);

const GalleryGrid = () => {
  const locale = useShIntlLocale();
  const professionFirstImages = useProfessionFirstImages(locale);
  const [selectedProfession, setSelectedProfession] = useState<
    ProfessionShootingType
  >();

  const renderProfession = (
    profession: ProfessionShootingType,
    maxWidth: GalleryImageMaxWidth,
  ) => {
    return (
      <GalleryGridCover
        label={getProfessionMessage(profession, 'short')}
        image={forceImageMaxWidth(professionFirstImages[profession], maxWidth)}
        onClick={() => setSelectedProfession(profession)}
      />
    );
  };

  return (
    <>
      <div className="grid-gallery__container">
        <ApplyMountEffect index={0} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="top-left-up">
              {renderProfession('hotel', 610)}
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={2} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="top-left-mid">
              {renderProfession('retail', 610)}
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={1} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="top-right">
              {renderProfession('commercialRealEstate', 610)}
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={3} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="order">
              <p className="invert">
                <ShText message={FrontCommonMessages.galleryP1} />
                <br />
                <ShText message={FrontCommonMessages.galleryP2} />
              </p>
              <p>
                <AppInternalLink
                  to={AppInternalPaths.order}
                  className="invert link"
                >
                  <ShText message={FrontCommonMessages.galleryCta} />
                </AppInternalLink>
              </p>
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={5} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="bottom-right">
              {renderProfession('architect', 810)}
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={4} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="bottom-left-mid">
              {renderProfession('restaurant', 400)}
            </div>
          )}
        </ApplyMountEffect>
        <ApplyMountEffect index={6} effect={cardRevealEffect}>
          {(ref) => (
            <div ref={ref} className="bottom-left-down">
              {renderProfession('residentialRealEstate', 400)}
            </div>
          )}
        </ApplyMountEffect>
      </div>
      <ProfessionGalleryModal
        selectedProfession={selectedProfession}
        onClose={() => setSelectedProfession(undefined)}
      />
    </>
  );
};

export default GalleryGrid;
